import { useId, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { foldDefault, rememberFold } from "../lib/fold";

interface Props {
  /** Nøkkelen valget huskes under. Stabil, ikke generert. */
  id: string;
  title: string;
  icon?: ReactNode;
  /**
   * Svaret panelet gir, vist i hodet når det er lagt sammen.
   *
   * Ikke pynt. Å legge sammen skal skjule utledningen og aldri svaret — uten
   * dette måtte man åpne hvert panel for å vite om det sto noe der, og da har
   * man byttet en lang side mot en side full av lukkede dører.
   */
  summary?: ReactNode;
  /**
   * Om panelet står åpent på en bred skjerm før brukeren har valgt noe.
   *
   * Standarden er nei. Meldt fra bruk: «sørg for at alle disse er lukket når vi
   * laster siden». Tre panel som alle spretter opp er en side man må rulle
   * forbi før man ser hva appen er — og et sammenlagt panel sier fortsatt hva
   * det inneholder, fordi `summary` står i hodet. Se lib/fold.ts.
   */
  openOnWide?: boolean;
  /**
   * Om panelet står åpent også på en smal skjerm.
   *
   * Unntaket fra «mobil starter sammenlagt», og det er ment å være sjeldent:
   * bare der innholdet endrer turen framfor å opplyse om den. Se `foldDefault`.
   */
  openOnNarrow?: boolean;
  children: ReactNode;
}

/**
 * Et panel som kan legges sammen, med svaret sitt i hodet.
 *
 * Se lib/fold.ts for hvorfor mobil starter lukket og hvordan valget huskes.
 */
export function FoldPanel({
  id,
  title,
  icon,
  summary,
  openOnWide = false,
  openOnNarrow = false,
  children,
}: Props) {
  /*
   * Tilstanden settes i en initialisator og ikke i en effekt.
   *
   * Med en effekt ville panelet rendret åpent og så klappet igjen på mobil —
   * et hopp i oppsettet ved hver lasting. `useState` med en funksjon leser
   * lagringen og skjermbredden før første maling.
   */
  const [open, setOpen] = useState(() => foldDefault(id, openOnWide, openOnNarrow));
  const bodyId = `${useId()}-body`;

  /*
   * Valget lagres når brukeren *tar* det, ikke når panelet tegnes.
   *
   * Her sto `useEffect(() => rememberFold(id, open), [id, open])`, som skrev
   * tilstanden ved hver eneste montering — også når ingen hadde rørt panelet.
   * Følgen er stygg og usynlig: standarden den gang var «åpen på bred skjerm»,
   * så *alle* som hadde lastet siden én gang fikk «open» lagret. Og et lagret
   * valg slår standarden med vilje, så da standarden ble endret til lukket,
   * fortsatte panelene å spette opp for nettopp de brukerne som hadde vært der
   * før — med en preferanse de aldri hadde uttrykt.
   *
   * Nå lagres bare et klikk. Den som aldri har rørt panelet får standarden, og
   * den som har det får sitt eget svar.
   */
  const toggle = () =>
    setOpen((was) => {
      rememberFold(id, !was);
      return !was;
    });

  return (
    <section className={`card fold ${open ? "fold--open" : "fold--shut"}`}>
      <h3 className="fold-head">
        <button
          type="button"
          className="fold-toggle"
          aria-expanded={open}
          /*
           * `aria-controls` bare når kroppen finnes.
           *
           * Innholdet fjernes fra treet når panelet er lukket (se under), så en
           * fast `aria-controls` pekte på en id som ikke fantes. Det var en
           * skjult finurlighet så lenge panelene startet åpne; nå er lukket
           * standarden, og da er det tilstanden en skjermleser møter først.
           */
          aria-controls={open ? bodyId : undefined}
          onClick={toggle}
        >
          {icon && <span className="fold-icon">{icon}</span>}
          <span className="fold-title">{title}</span>
          {/*
            Overskriftsverdien står bare når panelet er lukket. Åpent gjentar
            den det som står rett under, og en overskrift som gjentar seg selv
            leses som støy.
          */}
          {!open && summary && <span className="fold-summary">{summary}</span>}
          <ChevronDown className="fold-chevron" size={16} aria-hidden="true" />
        </button>
      </h3>

      {/*
        Innholdet fjernes fra treet når det er lukket framfor å skjules med
        CSS. Kartene og høydeprofilen tegner i canvas og lytter på
        vindusstørrelsen; et skjult kart som fortsatt måler seg selv får
        høyden null og kommer tilbake tomt når panelet åpnes igjen.
      */}
      {open && (
        <div className="fold-body" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  );
}
